import { useEffect, useState } from 'react';
import { productApiClient } from '../../clients/ProductAPIClient';
import { IOptions } from '../../data/models/option';
import { logger } from '../../utils/Logger';
import Select from './Select/Select';

type OrganizationTypeaheadProps = {
  resetTypeahead?: boolean;
  setResetTypeahead?: (resetTypeahead: boolean) => void;
  onSelectOrganizations: (orgs: IOptions[]) => void;
  initialOrgs?: IOptions[];
};

function OrganizationTypeahead({
  resetTypeahead,
  setResetTypeahead,
  onSelectOrganizations,
  initialOrgs = [],
}: OrganizationTypeaheadProps) {
  const [loading, setLoading] = useState(false);
  const [selectedOrgs, setSelectedOrgs] = useState<IOptions[]>(initialOrgs);
  const [inputValue, setInputValue] = useState('');
  const pageSize = 50;

  useEffect(() => {
    setSelectedOrgs(initialOrgs);
  }, [initialOrgs]);

  useEffect(() => {
    if (resetTypeahead) {
      setSelectedOrgs([]);
      setInputValue('');
      onSelectOrganizations([]);
      setResetTypeahead?.(false);
    }
  }, [onSelectOrganizations, resetTypeahead, setResetTypeahead]);

  const loadOrganizations = async (
    query: string,
    callback: (options: IOptions[]) => void
  ) => {
    setLoading(true);
    try {
      const results = await productApiClient.getLabel({
        value: query,
        limit: pageSize,
      });
      const limitedResults = results.slice(0, pageSize);
      callback(limitedResults);
      return limitedResults;
    } catch (e) {
      logger.error(`error loading orgs: ${e}`);
      callback([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  return (
    <Select
      id="organizationTypeahead"
      isMulti
      isClearable
      closeMenuOnSelect={false}
      isLoading={loading}
      initialOption={selectedOrgs}
      baseOptions={selectedOrgs}
      loadOptions={loadOrganizations}
      loadOptionsDebounceMs={500}
      inputValue={inputValue}
      menuIsOpen={inputValue.trim().length > 0}
      onInputChange={(value: string, actionMeta: { action: string }) => {
        if (actionMeta.action === 'input-change') {
          setInputValue(value);
        }

        if (actionMeta.action === 'set-value') {
          setInputValue('');
        }
      }}
      placeholder=""
      onChange={(orgs) => {
        const selected = Array.isArray(orgs) ? orgs : orgs ? [orgs] : [];
        setInputValue('');
        setSelectedOrgs(selected);
        onSelectOrganizations(selected);
      }}
    />
  );
}

export default OrganizationTypeahead;
